/**
 * Result type for tryCatch utility
 * Represents either a successful result with data or a failed result with an error
 */
export type Result<T, E = Error> =
  | { data: T; error: null }
  | { data: null; error: E }

/**
 * Wrapper function that catches errors and returns a Result object
 * instead of throwing. Works with both sync functions and async functions/promises.
 * Automatically logs errors to console.error.
 *
 * @param fn - A function (sync or async) or a Promise to execute
 * @returns A Result object containing either the data or the error
 *
 * @example
 * // Sync usage
 * const { data, error } = tryCatch(() => someOperation())
 *
 * // Async usage
 * const { data, error } = await tryCatch(async () => someAsyncOperation())
 * const { data, error } = await tryCatch(somePromise)
 *
 * // Void usage (just check for errors, ignore data)
 * const { error } = tryCatch(() => sideEffectOperation())
 * const { error } = await tryCatch(async () => asyncSideEffect())
 */
// Overload for sync functions
export function tryCatch<T, E = Error>(fn: () => T): Result<T, E>
// Overload for async functions
export function tryCatch<T, E = Error>(
  fn: () => Promise<T>,
): Promise<Result<T, E>>
// Overload for promises
export function tryCatch<T, E = Error>(
  promise: Promise<T>,
): Promise<Result<T, E>>
// Implementation
export function tryCatch<T, E = Error>(
  fn: (() => T | Promise<T>) | Promise<T>,
): Result<T, E> | Promise<Result<T, E>> {
  const handleError = (error: unknown): Result<T, E> => {
    console.error('tryCatch caught error:', error)
    return { data: null, error: error as E }
  }

  const handleAsync = async (promise: Promise<T>): Promise<Result<T, E>> => {
    try {
      const data = await promise
      return { data, error: null }
    } catch (error) {
      return handleError(error)
    }
  }

  // If it's a promise directly, handle it async
  if (fn instanceof Promise) {
    return handleAsync(fn)
  }

  // It's a function, try to execute it
  try {
    const result = fn()

    // If the result is a promise, handle it async
    if (result instanceof Promise) {
      return handleAsync(result)
    }

    // Sync result
    return { data: result, error: null }
  } catch (error) {
    return handleError(error)
  }
}
